import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Grid from "../../atoms/Grid/Grid";
import {
  APPROVED_ORDER_STATUS,
  COMPLETED_ORDER_STATUS,
  ORDER_STATUS,
  PENDING_ORDER_STATUS,
} from "../../../helpers/const";
import { getOrderListByParameters } from "../../../store/slices/OrderSlice/OrderSlice";
import { getCustomerLoginAs } from "../../../store/slices/CustomerSlice/CustomerSlice";

const orderHeaderAttributes = [
  {
    id: "OrderNumber",
    label: "Order #",
    tooltip: "Order Number",
    sortable: true,
    searchable: {
      label: "Search by Order Number",
      type: "text",
      width: 4,
    },
    width: "12%",
  },
  {
    id: "po",
    label: "PO",
    sortable: true,
    searchable: {
      label: "Search by Customer PO",
      type: "text",
      width: 2,
    },
    width: "10%",
  },
  {
    id: "invoiceNum",
    label: "Invoice #",
    sortable: true,
    searchable: {
      label: "Search by Invoice Number",
      type: "text",
      width: 2,
    },
    width: "10%",
  },
  {
    id: "dateOfOrder",
    label: "Date",
    tooltip: "Date of Order",
    sortable: true,
    width: "12%",
  },
  {
    id: "total",
    label: "Total",
    sortable: true,
    width: "10%",
  },
  {
    id: "agent",
    label: "Agent",
    sortable: true,
    width: "10%",
  },
  {
    id: "warehouseName",
    label: "Wh",
    tooltip: "Warehouse Name",
    sortable: true,
    width: "8%",
  },
  {
    id: "customer",
    label: "Customer",
    sortable: true,
    searchable: {
      label: "Search by Customer Name",
      type: "text",
      width: 2,
    },
    width: "15%",
  },
  {
    id: "status",
    label: "Status",
    sortable: true,
    width: "12%",
  },
  {
    id: "details",
    label: "Details",
    sortable: false,
    width: "8%",
  },
];

const returnRealAttributeName = (attr) => {
  switch (attr) {
    case "OrderNumber":
      return "suborder_num";
    case "po":
      return "customer_po";
    case "bol":
      return "bill_of_lading";
    case "pcs":
      return "product_code";
    case "customer":
      return "customer_name";
    case "dateOfOrder":
      return "date_of_order";
    case "warehouseName":
      return "warehouse_id";
    case "invoiceNum":
      return "customer_invoice_num";
    default:
      return attr;
  }
};

const fetchOrderData = async (dispatch, params) => {
  const orderParams = {
    customerOrderNum: params.OrderNumber,
    page: params.page,
    customerNum: params.customerNumbers, // This will be comma-delimited string
    customer_invoice_num: params.invoiceNum,
    customer_po: params.po,
    customerName: params.customer,
    productCode: params.pcs,
    description: params.description,
    dropShipAddress: params.dropShipAddress,
    orderBy: returnRealAttributeName(params.sortBy),
    sortOrder: params.sortOrder === "desc" ? "dsc" : "asc",
    orderStatus: params.select,
    from: params.from,
    to: params.to,
  };

  const result = await dispatch(getOrderListByParameters(orderParams));

  if (!result?.error) {
    return {
      count: result.payload.count,
      results: result.payload.results,
    };
  }

  return { count: 0, results: [] };
};

const mapOrderData = (orders) => {
  return orders.map((d) => ({
    id: d.customer_order_num,
    OrderNumber: d.customer_order_num,
    po: d?.customer_po || "-",
    invoiceNum:
      d.customer_invoice_num && d.customer_invoice_num !== "0"
        ? d.customer_invoice_num
        : "-",
    dateOfOrder: dayjs(d.date_of_order).format("MM/DD/YYYY"),
    total: `$${parseFloat(d.total || 0).toFixed(2)}`,
    agent: d.took_by || "-",
    warehouseName: d.warehouse_name || "-",
    customer: d.customer_name ? (
      <Link
        to={`/customers/${d.customer_id}`}
        className="text-[var(--primary-color)] hover:text-[var(--primary-color-dark)] underline"
      >
        {d.customer_name}
      </Link>
    ) : (
      <span>-</span>
    ),
    status: (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          d.status === COMPLETED_ORDER_STATUS.id
            ? "bg-green-100 text-green-800"
            : d.status === PENDING_ORDER_STATUS.id
              ? "bg-yellow-100 text-yellow-800"
              : d.status === APPROVED_ORDER_STATUS.id
                ? "bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-800"
        }`}
      >
        {ORDER_STATUS.find((item) => item.id === d.status)?.label || "Unknown"}
      </span>
    ),
    details: (
      <Link
        to={`/view-order/${d.customer_order_num}`}
        target="_blank"
        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-[var(--primary-color)] hover:bg-[var(--primary-color-dark)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)]"
      >
        View
      </Link>
    ),
  }));
};

export default function OrderHistory() {
  const dispatch = useDispatch();
  const { loginOptions } = useSelector((state) => state.customer);

  // Fetch customer list on component mount
  useEffect(() => {
    dispatch(getCustomerLoginAs());
  }, [dispatch]);

  // Transform loginOptions to dropdown options
  const customerOptions =
    loginOptions?.map((customer) => ({
      id: customer.id.toString(),
      value: customer.id.toString(),
      label: `${customer.id} - ${customer.label}`,
      customerName: customer.label,
    })) || [];

  const customFilters = [
    {
      id: "customerNumbers",
      label: "Filter by Customer Numbers",
      placeholder: "Select customer numbers...",
      type: "autocomplete-multiselect",
      options: customerOptions,
    },
  ];

  return (
    <div className="p-6">
      <div className="mt-6">
        <Grid
          title="Order History"
          headerAttributes={orderHeaderAttributes}
          fetchDataFunction={fetchOrderData}
          mapDataFunction={mapOrderData}
          loadingSelector={(state) => state.loaderSpinner?.loading}
          rowsPerPage={50}
          defaultSortBy="OrderNumber"
          defaultSortOrder="desc"
          showFilters={true}
          showDateFilter={true}
          showPagination={true}
          customFilters={customFilters}
        />
      </div>
    </div>
  );
}
